import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from './authClient';
import Lenis from 'lenis';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function SmoothScrollLayout({ children }) {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Klasický smooth ease
      smoothTouch: false, // Na dotykáčích necháváme nativní scroll
    });

    lenisRef.current = lenis;

    // Propojení Lenis a GSAP ScrollTriggeru
    lenis.on('scroll', ScrollTrigger.update);

    const update = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0, 0);

    return () => {
      lenis.destroy();
      gsap.ticker.remove(update);
    };
  }, []);

  useEffect(() => {
    // Pokud URL obsahuje hash (např. #rezervace), odscrollujeme na danou sekci místo na začátek.
    // Hash nemusí být platný CSS selektor (stará routa #/admin) – querySelector by
    // vyhodil SyntaxError a shodil celou aplikaci do ErrorBoundary.
    const findHashTarget = () => {
      try {
        return document.querySelector(location.hash);
      } catch {
        return null;
      }
    };

    if (location.hash) {
      const scrollToHash = () => {
        const el = findHashTarget();
        if (!el) return false;
        if (lenisRef.current) {
          lenisRef.current.scrollTo(el, { offset: -80 });
        } else {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        return true;
      };
      // Cíl nemusí být hned po přechodu route k dispozici (lazy load) – zkusíme znovu.
      if (scrollToHash()) return;
      const timeout = setTimeout(scrollToHash, 400);
      return () => clearTimeout(timeout);
    }

    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return <>{children}</>;
}

// Lazy loading stránek
const LandingPage = lazy(() => import('./pages/LandingPage'));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const OrderPage = lazy(() => import('./pages/OrderPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

function ProtectedRoute({ children }) {
  // Token (vč. expirace) ověřujeme synchronně z localStorage – žádné async volání.
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

import PageTransition from './components/PageTransition';
import { AnimatePresence } from 'framer-motion';
import CookieConsent from './components/CookieConsent';

function AnimatedRoutes() {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
        <Route path="/menu" element={<PageTransition><MenuPage /></PageTransition>} />
        <Route path="/objednavka" element={<PageTransition><OrderPage /></PageTransition>} />
        <Route path="/o-nas" element={<PageTransition><AboutPage /></PageTransition>} />
        <Route path="/kontakt" element={<PageTransition><ContactPage /></PageTransition>} />
        <Route path="/admin" element={<ProtectedRoute><PageTransition><AdminPage /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute><PageTransition><AdminPage /></PageTransition></ProtectedRoute>} />
        <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
        <Route path="/sledovani-objednavky" element={<PageTransition><OrderTrackingPage /></PageTransition>} />
        <Route path="/sledovani-objednavky/:orderId" element={<PageTransition><OrderTrackingPage /></PageTransition>} />
        {/* Fallback route for any unmatched routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

// Cookie lišta se nezobrazuje v administraci ani na přihlášení (jen veřejný web).
function CookieGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin') || pathname === '/login') return null;
  return <CookieConsent />;
}

function App() {
  return (
    <Router>
      <SmoothScrollLayout>
        <Suspense fallback={
          <div className="flex justify-center items-center min-h-screen bg-neutral-950">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-gold-400/20 border-t-gold-400 rounded-full animate-spin"></div>
              <span className="text-gold-400 font-jost tracking-widest uppercase text-sm">Načítám...</span>
            </div>
          </div>
        }>
          <AnimatedRoutes />
        </Suspense>
      </SmoothScrollLayout>
      <CookieGate />
    </Router>
  );
}

export default App; 